// ActivityFeed — small right-rail list mirroring the mockup.

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  FaCheckCircle,
  FaUserPlus,
  FaStar,
  FaBullhorn,
  FaCircle,
} from 'react-icons/fa';

const KIND_META = {
  diagnostic_completed: {
    icon: <FaCheckCircle />,
    title: (a) => `Nouveau diagnostic complété`,
    subtitle: (a) =>
      `par ${a.actor_label || 'Utilisateur'}${
        a.payload?.title ? ` — ${a.payload.title}` : ''
      }`,
    tone: 'mist',
  },
  participant_joined: {
    icon: <FaUserPlus />,
    title: () => 'Nouveau participant',
    subtitle: (a) =>
      `${a.actor_label || 'Quelqu\'un'} a rejoint la campagne`,
    tone: 'sky',
  },
  use_case_identified: {
    icon: <FaStar />,
    title: () => 'Cas d\'usage identifié',
    subtitle: (a) =>
      `« ${a.payload?.title || 'Sans titre'} » — score ${a.payload?.score ?? '—'}/100`,
    tone: 'amber',
  },
  campaign_created: {
    icon: <FaBullhorn />,
    title: () => 'Campagne créée',
    subtitle: (a) => `${a.actor_label || 'Admin'}`,
    tone: 'lilac',
  },
};

function formatRelative(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffSec = Math.max(0, (Date.now() - then) / 1000);
  if (diffSec < 60)        return 'à l\'instant';
  if (diffSec < 3600)      return `il y a ${Math.floor(diffSec / 60)} min`;
  if (diffSec < 86400)     return `il y a ${Math.floor(diffSec / 3600)} h`;
  return `il y a ${Math.floor(diffSec / 86400)} j`;
}

export default function ActivityFeed({ activities = [], loading = false }) {
  const { t } = useTranslation();
  return (
    <aside className="dia-activity">
      <header className="dia-card-head">
        <h3>{t('diagia.feed.title', 'Activité récente')}</h3>
      </header>

      {loading && (
        <ul className="dia-activity-list">
          {[1, 2, 3].map((i) => (
            <li key={i} className="dia-activity-item is-loading">
              <span className="dia-activity-icon">
                <FaCircle />
              </span>
              <div className="dia-activity-body">
                <span className="dia-activity-title">—</span>
                <span className="dia-activity-sub">—</span>
              </div>
            </li>
          ))}
        </ul>
      )}

      {!loading && activities.length === 0 && (
        <p className="dia-empty">{t('diagia.feed.empty', "Pas encore d'activité. Lance un diagnostic pour démarrer.")}</p>
      )}

      {!loading && activities.length > 0 && (
        <ul className="dia-activity-list">
          {activities.map((a) => {
            const meta = KIND_META[a.kind] || {
              icon: <FaCircle />,
              title: () => 'Événement',
              subtitle: () => '',
              tone: 'mist',
            };
            return (
              <li key={a.id} className={`dia-activity-item dia-activity-${meta.tone}`}>
                <span className="dia-activity-icon">{meta.icon}</span>
                <div className="dia-activity-body">
                  <span className="dia-activity-title">{meta.title(a)}</span>
                  <span className="dia-activity-sub">{meta.subtitle(a)}</span>
                </div>
                <span className="dia-activity-time">{formatRelative(a.created_at)}</span>
              </li>
            );
          })}
        </ul>
      )}

      <button type="button" className="dia-activity-footer" disabled>
        Voir toute l'activité
      </button>
    </aside>
  );
}
